import { Fragment, useRef, useState } from "react";

import { faTrashCan, faWarning } from "@fortawesome/pro-regular-svg-icons";
import { capitalize, get } from "lodash-es";
import {
  Controller,
  FieldError,
  useController,
  useFormContext,
  useWatch,
} from "react-hook-form";

import {
  JSONSchemaPropertyDefinition,
  ManifestJSONSchema,
} from "src/api/connectApi/manifestTypes";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Switch } from "src/base-components/Switch";
import { TagsInput } from "src/base-components/TagsInput";
import { Textarea } from "src/base-components/Textarea";
import {
  AddObjectFieldDropdown,
  OptionalObjectProperty,
} from "src/connections/config/manifest/common/AddObjectFieldDropdown";
import { EditSecretButton } from "src/connections/config/manifest/common/EditSecretButton";
import { EnumField } from "src/connections/config/manifest/common/EnumField";
import { getValidationRules } from "src/connections/config/manifest/common/ValidationRules";
import {
  FormPath,
  ManifestFormType,
} from "src/connections/config/manifest/types";
import {
  isFieldBoolean,
  isFieldOfNullableType,
  isFieldTagsInput,
  isFieldTextarea,
} from "src/connections/config/manifest/utils";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { TextWithMarkdownLinks } from "src/integrationNode/editorComponents/TextWithMarkdownLinks";

type ManifestForm = {
  schema?: ManifestJSONSchema;
  prefix: string;
  isConnectionUpdate?: boolean;
  formPath: FormPath;
  descriptionVariant?: "inline" | "tooltip";
  // Opt-in: renders object-typed properties as expandable field groups.
  // Only the resource config form enables this; credential forms keep the flat behavior.
  allowObjectFields?: boolean;
};
export const Form: React.FC<ManifestForm> = ({
  schema,
  prefix,
  formPath,
  isConnectionUpdate,
  descriptionVariant = "inline",
  allowObjectFields = false,
}) => {
  const form = useFormContext<ManifestFormType>();
  const [editableFields, setEditableFields] = useState<{
    [key: string]: boolean;
  }>(() => {
    if (!schema?.secrets) return {};

    const currentValues = get(form.getValues(), formPath);

    const isEmptyEnvironment = () => {
      if (!currentValues) return true;
      return Object.values(currentValues).every((field) => !field);
    };

    return schema.secrets.reduce(
      (acc, secretKey) => {
        const value = get(currentValues, secretKey);
        acc[secretKey] = isEmptyEnvironment() || !value;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  });

  const toggleEditable = (key: string) => {
    setEditableFields((prev) => {
      const newState = { ...prev, [key]: !prev[key] };
      // We reset the value to an empty string when the secret
      // is toggled to editable.
      if (!prev[key]) {
        form.setValue(`${formPath}.${key}`, "");
      }
      return newState;
    });
  };

  if (!schema) return null;

  const secretFields = schema?.secrets || [];
  const fields = Object.entries(schema?.properties || {}).map(
    ([key, value]) => {
      if (typeof value === "boolean") {
        throw new Error("Boolean is not supported");
      }
      const schemaValue = value as ManifestJSONSchema;
      const isRequired = schema.required?.includes(key);

      if (
        allowObjectFields &&
        value.renderAsField === true &&
        (isFieldOfNullableType(schemaValue, "object") ||
          isFieldOfNullableType(schemaValue, "array"))
      ) {
        return (
          <JsonField
            key={key}
            descriptionVariant={descriptionVariant}
            fieldKey={key}
            formPath={formPath}
            isRequired={!!isRequired}
            prefix={prefix}
            schema={value}
          />
        );
      }

      if (
        allowObjectFields &&
        isFieldOfNullableType(schemaValue, "object") &&
        value.renderAsField !== true
      ) {
        return (
          <ObjectField
            key={key}
            descriptionVariant={descriptionVariant}
            fieldKey={key}
            formPath={formPath}
            isRequired={!!isRequired}
            prefix={prefix}
            schema={schemaValue}
          />
        );
      }

      const inputFieldPlaceholder =
        value.example !== undefined
          ? `e.g. ${value.example}`
          : `Enter ${(value.title || key).toLowerCase()}`;

      const isSecret = secretFields.includes(key);
      const fieldError = get(form.formState.errors, `${formPath}.${key}`) as
        | FieldError
        | undefined;
      const isFieldErrored = !!fieldError;

      const currentValue = form.getValues(`${formPath}.${key}`);
      const isDisabledSecretInput =
        isConnectionUpdate &&
        isSecret &&
        !editableFields[key] &&
        Boolean(currentValue);

      const validationRules = getValidationRules(
        schemaValue,
        !!isRequired,
        key,
      );

      const errorMessage = fieldError?.message || "Invalid value";

      const descriptionProps =
        descriptionVariant === "tooltip"
          ? {
              helpTooltipBody: value.description ? (
                <TextWithMarkdownLinks text={value.description} />
              ) : undefined,
              helpDataLoc: `${prefix}-${key}-description`,
            }
          : {
              description: (
                <TextWithMarkdownLinks text={value.description || ""} />
              ),
            };

      if ("enum" in value && value.widget !== "text") {
        return (
          <Fragment key={key}>
            <FormItem
              key={key}
              dataLoc={`${prefix}-${key}`}
              gap={isFieldErrored ? "xxs" : "sm"}
              {...descriptionProps}
              id={`${prefix}_${key}`}
              isRequired={!!isRequired}
              label={value.title || capitalize(key)}
            >
              <EnumField
                fieldKey={key}
                formPath={formPath}
                isRequired={!!isRequired}
                jsonSchemaDefinition={value}
                prefix={prefix}
              />
            </FormItem>
            {isFieldErrored && <ErrorHint>{errorMessage}</ErrorHint>}
          </Fragment>
        );
      }

      if (isFieldBoolean(value)) {
        return (
          <Fragment key={key}>
            <FormItem
              key={key}
              className="flex items-center justify-between"
              dataLoc={`${prefix}-${key}`}
              gap={isFieldErrored ? "xxs" : "sm"}
              {...descriptionProps}
              id={`${prefix}_${key}`}
              isRequired={!!isRequired}
              label={value.title || capitalize(key)}
            >
              <Controller
                control={form.control}
                name={`${formPath}.${key}`}
                render={({ field: { value, onChange } }) => (
                  <div className="ml-2">
                    <Switch enabled={value as boolean} onChange={onChange} />
                  </div>
                )}
                rules={validationRules}
              />
            </FormItem>
            {isFieldErrored && <ErrorHint>{errorMessage}</ErrorHint>}
          </Fragment>
        );
      }

      if (isFieldTextarea(value)) {
        return (
          <Fragment key={key}>
            <FormItem
              key={key}
              dataLoc={`${prefix}-${key}`}
              gap={isFieldErrored ? "xxs" : "sm"}
              {...descriptionProps}
              id={`${prefix}_${key}`}
              isRequired={!!isRequired}
              label={value.title || capitalize(key)}
            >
              <Textarea
                errored={isFieldErrored}
                {...form.register(`${formPath}.${key}`, validationRules)}
                placeholder={inputFieldPlaceholder}
                autoGrow
              />
            </FormItem>
            {isFieldErrored && <ErrorHint>{errorMessage}</ErrorHint>}
          </Fragment>
        );
      }

      if (isFieldTagsInput(value)) {
        return (
          <Fragment key={key}>
            <FormItem
              key={key}
              dataLoc={`${prefix}-${key}`}
              gap={isFieldErrored ? "xxs" : "sm"}
              {...descriptionProps}
              id={`${prefix}_${key}`}
              isRequired={!!isRequired}
              label={value.title || capitalize(key)}
            >
              <Controller
                control={form.control}
                name={`${formPath}.${key}`}
                render={({ field: controlledField }) => (
                  <TagsInput
                    value={
                      Array.isArray(controlledField.value)
                        ? controlledField.value
                        : []
                    }
                    onChange={controlledField.onChange}
                  />
                )}
              />
            </FormItem>
            {isFieldErrored && <ErrorHint>{errorMessage}</ErrorHint>}
          </Fragment>
        );
      }

      return (
        <Fragment key={key}>
          <FormItem
            key={key}
            dataLoc={`${prefix}-${key}`}
            gap={isFieldErrored ? "xxs" : "sm"}
            {...descriptionProps}
            id={`${prefix}_${key}`}
            isRequired={!!isRequired}
            label={value.title || capitalize(key)}
          >
            <div className="flex w-full">
              <div className="flex-1">
                <Input
                  data-loc={`${prefix}-${key}-input`}
                  disabled={isDisabledSecretInput}
                  errored={isFieldErrored}
                  placeholder={inputFieldPlaceholder}
                  suffixIcon={isFieldErrored ? { icon: faWarning } : undefined}
                  type={isSecret ? "password" : "text"}
                  fullWidth
                  {...form.register(`${formPath}.${key}`, validationRules)}
                />
              </div>
              {isDisabledSecretInput && (
                <EditSecretButton
                  secretKey={key}
                  secretName={value.title || ""}
                  toggleEditable={() => toggleEditable(key)}
                />
              )}
            </div>
          </FormItem>
          {isFieldErrored && <ErrorHint>{errorMessage}</ErrorHint>}
        </Fragment>
      );
    },
  );

  const optionalObjectProperties: OptionalObjectProperty[] = allowObjectFields
    ? Object.entries(schema.properties || {}).flatMap(([key, value]) =>
        typeof value === "object" &&
        value !== null &&
        isFieldOfNullableType(value, "object") &&
        value.renderAsField !== true &&
        !schema.required?.includes(key)
          ? [{ key, schema: value }]
          : [],
      )
    : [];

  return (
    <>
      {fields}
      {optionalObjectProperties.length > 0 && (
        <AddObjectFieldDropdown
          formPath={formPath}
          optionalObjects={optionalObjectProperties}
          prefix={prefix}
        />
      )}
    </>
  );
};

type JsonFieldProps = {
  fieldKey: string;
  schema: JSONSchemaPropertyDefinition;
  formPath: FormPath;
  prefix: string;
  isRequired: boolean;
  descriptionVariant: "inline" | "tooltip";
};

const JsonField: React.FC<JsonFieldProps> = ({
  fieldKey,
  schema,
  formPath,
  prefix,
  isRequired,
  descriptionVariant,
}) => {
  const form = useFormContext<ManifestFormType>();
  const fieldPath = `${formPath}.${fieldKey}` as const;
  const schemaTypes = Array.isArray(schema.type) ? schema.type : [schema.type];
  const expectedContainerType = schemaTypes.includes("array")
    ? "array"
    : "object";
  // Parse failures live in a ref so the validate rule sees the current state
  // at submit time — local state alone would let handleSubmit pass on the
  // last committed value while the editor shows unparseable text.
  const parseErrorRef = useRef<string | null>(null);
  const { field, fieldState } = useController({
    control: form.control,
    name: fieldPath,
    rules: {
      required: isRequired,
      validate: () => parseErrorRef.current ?? true,
    },
  });
  const [text, setText] = useState<string>(() =>
    field.value === undefined || field.value === null
      ? ""
      : JSON.stringify(field.value, null, 2),
  );
  const [parseError, setParseErrorState] = useState<string | null>(null);

  const setParseError = (error: string | null) => {
    parseErrorRef.current = error;
    setParseErrorState(error);
  };

  const matchesSchemaType = (parsed: unknown): boolean =>
    schemaTypes.some((type) =>
      type === "array"
        ? Array.isArray(parsed)
        : type === "null"
          ? parsed === null
          : typeof parsed === "object" &&
            parsed !== null &&
            !Array.isArray(parsed),
    );

  const handleChange = (nextText: string) => {
    setText(nextText);
    if (nextText.trim() === "") {
      setParseError(null);
      field.onChange(undefined);
      return;
    }
    try {
      const parsed: unknown = JSON.parse(nextText);
      if (!matchesSchemaType(parsed)) {
        setParseError(`Must be a JSON ${expectedContainerType}`);
        return;
      }
      // Clear the ref before onChange: RHF revalidates errored fields on
      // change, and the validate rule must not see the previous parse failure.
      setParseError(null);
      field.onChange(parsed);
    } catch {
      setParseError("Must be valid JSON");
    }
  };

  const errored = !!parseError || !!fieldState.error;
  const errorMessage = parseError ?? "Required";

  const descriptionProps =
    descriptionVariant === "tooltip"
      ? {
          helpTooltipBody: schema.description ? (
            <TextWithMarkdownLinks text={schema.description} />
          ) : undefined,
          helpDataLoc: `${prefix}-${fieldKey}-description`,
        }
      : {
          description: (
            <TextWithMarkdownLinks text={schema.description || ""} />
          ),
        };

  return (
    <Fragment key={fieldKey}>
      <FormItem
        dataLoc={`${prefix}-${fieldKey}`}
        gap={errored ? "xxs" : "sm"}
        {...descriptionProps}
        id={`${prefix}_${fieldKey}`}
        isRequired={isRequired}
        label={schema.title || capitalize(fieldKey)}
      >
        <div className="rounded-lg border border-gray-200 bg-white px-2 py-1">
          <CodeEditor
            basicSetup={{ autocompletion: false, completionKeymap: false }}
            dataLoc={`${prefix}-${fieldKey}-json-input`}
            drawErrorBorder={errored}
            language="json"
            placeholder={
              schema.example !== undefined
                ? `${JSON.stringify(schema.example, null, 2)}`
                : '{ "key": "value" }'
            }
            value={text}
            lineWrapping
            onBlur={field.onBlur}
            onChange={handleChange}
          />
        </div>
      </FormItem>
      {errored && <ErrorHint>{errorMessage}</ErrorHint>}
    </Fragment>
  );
};

type ObjectFieldProps = {
  fieldKey: string;
  schema: ManifestJSONSchema;
  formPath: FormPath;
  prefix: string;
  isRequired: boolean;
  descriptionVariant: "inline" | "tooltip";
};

// Renders an object-typed schema property as a field group. Optional objects
// start collapsed as an "Add {name}" button; adding materializes an empty object
// in the form state and deleting sets it back to undefined, so nested required
// rules only apply while the group is present.
const ObjectField: React.FC<ObjectFieldProps> = ({
  fieldKey,
  schema,
  formPath,
  prefix,
  isRequired,
  descriptionVariant,
}) => {
  const form = useFormContext<ManifestFormType>();
  const objectPath = `${formPath}.${fieldKey}` as const;
  const currentValue = useWatch({ control: form.control, name: objectPath });
  const label = schema.title || capitalize(fieldKey);

  const handleDelete = () => {
    // Clear + unregister (rather than setValue(undefined)) so nested field
    // registrations and their validation rules are fully removed, matching
    // how ManifestEnvironmentsConfig removes conditional sections.
    form.clearErrors(objectPath);
    form.unregister(objectPath);
  };

  if (!isRequired && currentValue === undefined) {
    return null;
  }

  return (
    <div
      className="mb-6 rounded-lg border border-gray-200 p-4"
      data-loc={`${prefix}-${fieldKey}-object`}
    >
      <div className="mb-4 flex items-center justify-between gap-x-2 border-gray-200 border-b pb-2">
        <div className="flex flex-col">
          <span className="font-inter-normal-12px text-gray-700 uppercase">
            {label}
          </span>
          {schema.description && (
            <span className="font-inter-normal-12px text-gray-500">
              <TextWithMarkdownLinks text={schema.description} />
            </span>
          )}
        </div>
        {!isRequired && (
          <div className="shrink-0">
            <Button
              dataLoc={`${prefix}-${fieldKey}-delete`}
              iconLeft={faTrashCan}
              size="sm"
              variant="secondary"
              onClick={handleDelete}
            >
              Delete {label.toLowerCase()}
            </Button>
          </div>
        )}
      </div>
      <Form
        descriptionVariant={descriptionVariant}
        formPath={objectPath}
        prefix={`${prefix}-${fieldKey}`}
        schema={schema}
        allowObjectFields
      />
    </div>
  );
};
